import React from 'react'
import { Link } from 'react-router-dom'

import { Alert, Cluster } from 'component-library-next'
import { RiFilterLine } from 'react-icons/ri'

import {
  DATE_TIME_FORMAT,
  formatDateTime,
  formatTimeOutput
} from '@app/common/helpers/DateTime/DateTime'
import { CourtCaseState } from '@app/graphql/types'
import { useIntl } from '@app/intl/Intl'
import { bemJoin } from '@app/lib/bem'
import { Chip } from '@app/lib/display/Chip/Chip'
import { CourtCaseStateChip } from '@app/lib/display/Chip/CourtCaseStateChip'
import { ProductType } from '@app/lib/display/ProductType/ProductType'
import { Button } from '@app/lib/inputs/Button/Button'
import { Card } from '@app/lib/layout/Card/Card'
import { CardBlock } from '@app/lib/layout/Card/CardBlock'
import { CardHeader } from '@app/lib/layout/Card/CardHeader'
import { LineItem } from '@app/lib/layout/LineItem/LineItem'
import { Sidebar } from '@app/lib/layout/Sidebar/Sidebar'
import { Table, TableColumn } from '@app/lib/layout/Table/Table'
import { Heading } from '@app/lib/typography/Heading/Heading'
import { getCourtCasePath } from '@app/modules/CourtCases'
import { CourtCasesFilterForm } from '@app/modules/CourtCasesTables/Components/CourtCasesFilterForm'
import {
  CourtCaseItem,
  useCourtCasesTable
} from '@app/modules/CourtCasesTables/hooks/useCourtCasesTable'

import './CourtCasesWidget.scss'

export const CourtCasesWidget = () => {
  const { formatMessage } = useIntl()
  const b = bemJoin('court-cases-widget')
  const {
    entries,
    loading,
    error,
    setShowFilter,
    showFilter,
    filterValues,
    setFilterValues,
    sorts,
    setSorts
  } = useCourtCasesTable()

  const columns: Array<TableColumn<CourtCaseItem>> = [
    {
      key: 'customerFullName',
      label: formatMessage({ id: 'CourtCasesTables.Column.Name' }),
      value: (item) => (
        <div className={b('customer-name')}>
          <Link to={getCourtCasePath(item.customer.id, item.uuid)}>
            {[item.customer.firstName, item.customer.lastName].filter(Boolean).join(' ')}
          </Link>
          {item.state === CourtCaseState.ClientFileApproved && (
            <Chip color={'info'} variant={'filled'} size={'xs'} minWidth={false}>
              NEU
            </Chip>
          )}
        </div>
      )
    },
    {
      key: 'state',
      label: formatMessage({ id: 'CourtCasesTables.Column.State' }),
      value: (item) => <CourtCaseStateChip item={item} />
    },
    {
      key: 'appointment',
      label: formatMessage({ id: `CourtCasesTables.Column.Appointment` }),
      sort: sorts.upcomingAppointment,
      onSortChange: (direction) => setSorts({ upcomingAppointment: direction }),
      value: (item) =>
        item.upcomingAppointment ? formatTimeOutput(item.upcomingAppointment) : '---'
    },
    {
      key: 'createdAt',
      label: formatMessage({ id: 'CourtCasesTables.Column.CreatedAt' }),
      sort: sorts.createdAt,
      onSortChange: (direction) => setSorts({ createdAt: direction }),
      value: (item) =>
        item.createdAt ? formatDateTime(item.createdAt, DATE_TIME_FORMAT.DATE_FULL) : ''
    },
    {
      key: 'product',
      label: formatMessage({ id: 'CourtCasesTables.Column.Product' }),
      value: (item) => {
        return <ProductType {...item.customer.membership?.membershipType} />
      }
    }
  ]

  return (
    <Card>
      <CardHeader>
        <LineItem
          label={
            <Heading size={'sm'}>{formatMessage({ id: `CourtCasesTables.TableTitle` })}</Heading>
          }
          actions={
            <Cluster>
              <Button size='sm' variant='outlined' onClick={() => setShowFilter((prev) => !prev)}>
                <RiFilterLine />
                <span>
                  {formatMessage({
                    id: showFilter ? 'CourtCasesTables.HideFilter' : 'CourtCasesTables.ShowFilter'
                  })}
                </span>
              </Button>
            </Cluster>
          }
        />
      </CardHeader>
      {error && (
        <CardBlock>
          <Alert color={Alert.colors.warning}>
            {formatMessage({ id: 'CourtCasesTables.Error' })}
          </Alert>
        </CardBlock>
      )}
      <Sidebar
        sidebar={<CourtCasesFilterForm initialValues={filterValues} onSubmit={setFilterValues} />}
        isVisible={showFilter}
      >
        <Table
          columns={columns}
          items={entries || []}
          loading={loading}
          noResults={formatMessage({ id: 'CourtCasesTables.NoResults' })}
        />
      </Sidebar>
    </Card>
  )
}
